import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { AgentFileEditor } from '../components/AgentFileEditor'
import { ChatPanelMaster } from './ChatPage/ChatPanelMaster'
import { TopNavigation } from '../components/TopNavigation'
// useMediaQuery removed
import { agentService, type Agent } from '../services/agentService'
import { AgentSidebar } from '../components/AgentProfile/AgentSidebar'
import { motion, AnimatePresence } from 'framer-motion'
import { useChatLayout } from '../hooks/useChatLayout'
import { ErrorBoundary } from '../components/ErrorBoundary'

export function AgentConfigPage() {
    const { handle } = useParams<{ handle: string }>()
    // isMobile check removed as chat is no longer rendered

    const [agent, setAgent] = useState<Agent | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isChatOpen, setIsChatOpen] = useState(true)
    const [isResizing, setIsResizing] = useState(false)
    const [isChatFullscreen, setIsChatFullscreen] = useState(false)

    // Use chat layout hook for persistent width
    const { chatWidth, setChatWidth } = useChatLayout('agent_config', 'right', 450)

    useEffect(() => {
        const loadAgent = async () => {
            if (!handle) return

            try {
                setIsLoading(true)
                const agentData = await agentService.getAgentByHandle(handle)
                setAgent(agentData)
            } catch (error) {
                console.error('Error loading agent:', error)
            } finally {
                setIsLoading(false)
            }
        }

        loadAgent()
    }, [handle])

    // Handle chat resize
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault()
        setIsResizing(true)
    }

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return

            const newWidth = window.innerWidth - e.clientX
            // Limit width between 300px and 800px
            if (newWidth >= 300 && newWidth <= 800) {
                setChatWidth(newWidth)
            }
        }

        const handleMouseUp = () => {
            setIsResizing(false)
        }

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing, setChatWidth])


    return (
        <div className="flex flex-col h-screen overflow-hidden bg-white dark:bg-black">
            {/* TopNavigation Header */}
            <TopNavigation />

            {/* Flex Row Container with min-h-0 to prevent vertical overflow */}
            <div className="flex-1 flex relative min-h-0">
                {/* Agent Sidebar - Always visible */}
                <AgentSidebar agent={agent} onChatClick={() => setIsChatOpen(!isChatOpen)} />

                {/* Main Content Area - Now compresses when chat opens */}
                {/* min-w-0 prevents flex child from overflowing horizontally */}
                <div className="flex-1 overflow-y-auto h-full transition-all duration-300 min-w-0">
                    {isLoading ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
                            </div>
                        </div>
                    ) : !agent ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                    Agente não encontrado
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    O agente "{handle}" não foi encontrado.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <AgentFileEditor agentId={agent.id} />
                    )}
                </div>

                {/* ChatPanelMaster - Single persistent instance */}
                <AnimatePresence>
                    {isChatOpen && agent && (
                        <motion.div
                            key="chat-panel"
                            {...({
                                initial: { opacity: 0 },
                                animate: { opacity: 1 },
                                exit: { opacity: 0 }
                            } as any)}
                            transition={{ duration: 0.2 }}
                            className={
                                isChatFullscreen
                                    ? 'absolute inset-0 bg-white dark:bg-black z-[90]'
                                    : 'border-l border-gray-200 dark:border-gray-800 shadow-xl bg-white dark:bg-black hidden lg:block overflow-hidden h-full flex flex-col'
                            }
                            style={!isChatFullscreen ? { width: chatWidth } : undefined}
                        >
                            {/* Resize Handle - Only in normal mode */}
                            {!isChatFullscreen && (
                                <div
                                    onMouseDown={handleMouseDown}
                                    className="absolute left-0 top-0 w-4 h-full cursor-ew-resize -translate-x-1.5 z-10 hover:bg-blue-500/10 transition-colors"
                                />
                            )}

                            <ErrorBoundary>
                                <ChatPanelMaster
                                    isOpen={true}
                                    onClose={() => setIsChatOpen(false)}
                                    onFullscreenChange={setIsChatFullscreen}
                                    isFullscreen={isChatFullscreen}
                                    agentId={agent.id}
                                    agentName={agent.name}
                                    agent={agent}
                                    isEmbedded={true}
                                    position="right"
                                />
                            </ErrorBoundary>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
